import { useRef, useState } from "react";
import { toast } from "sonner";
import { Camera, FileText, Image as ImageIcon, Loader2, Plus, Trash2, Upload } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { SearchableSelect } from "@/components/ui/searchable-select";
import { CameraModal } from "@/components/CameraModal";
import { createBleed } from "@/Api/bleeds";

const MAX_FILES = 5;
const MAX_FILE_SIZE = 10 * 1024 * 1024;
const ACCEPTED_TYPES = ["image/jpeg", "image/png", "image/webp", "application/pdf"];

/** Data de hoje no fuso local (yyyy-mm-dd). */
const today = () => new Date().toLocaleDateString("sv-SE");

interface SellerOption {
  id: number;
  name: string;
}

interface AddBleedProps {
  onCreated: () => void;
  /** Quem vê todas as sangrias (financeiro/admin) pode registrar em nome de um vendedor. */
  sellers?: SellerOption[];
}

export function AddBleed({ onCreated, sellers }: AddBleedProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [isCameraOpen, setIsCameraOpen] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [code, setCode] = useState("");
  const [bleedDate, setBleedDate] = useState(today());
  const [value, setValue] = useState(0);
  const [valueKey, setValueKey] = useState(0);
  const [observation, setObservation] = useState("");
  const [seller, setSeller] = useState<SellerOption | undefined>();
  const [files, setFiles] = useState<File[]>([]);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const fileInputRef = useRef<HTMLInputElement>(null);

  const selectSeller = !!sellers;

  const reset = () => {
    setCode("");
    setBleedDate(today());
    setValue(0);
    setValueKey((k) => k + 1);
    setObservation("");
    setSeller(undefined);
    setFiles([]);
    setErrors({});
  };

  const addFiles = (incoming: File[]) => {
    const valid: File[] = [];

    for (const file of incoming) {
      if (!ACCEPTED_TYPES.includes(file.type)) {
        toast.error(`${file.name}: formato não aceito (use JPG, PNG, WEBP ou PDF).`);
        continue;
      }
      if (file.size > MAX_FILE_SIZE) {
        toast.error(`${file.name}: o arquivo passa de 10 MB.`);
        continue;
      }
      valid.push(file);
    }

    setFiles((prev) => {
      const next = [...prev, ...valid];
      if (next.length > MAX_FILES) {
        toast.error(`Envie no máximo ${MAX_FILES} anexos.`);
      }
      return next.slice(0, MAX_FILES);
    });
    setErrors((prev) => ({ ...prev, files: "" }));
  };

  const validate = () => {
    const next: Record<string, string> = {};

    // Exatamente 5 dígitos; pode começar com zero (ex.: 01234)
    if (!/^\d{5}$/.test(code)) {
      next.code = "O código deve ter exatamente 5 dígitos numéricos.";
    }
    if (!bleedDate) next.bleed_date = "Informe a data da sangria.";
    if (!(value > 0)) next.value = "Informe o valor da sangria.";
    if (selectSeller && !seller) next.seller = "Selecione o vendedor.";
    if (files.length === 0) next.files = "O anexo é obrigatório.";

    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const handleSubmit = async () => {
    if (!validate()) return;

    setIsSaving(true);
    try {
      const { ok } = await createBleed({
        code,
        bleed_date: bleedDate,
        value,
        observation: observation.trim() || undefined,
        seller_id: seller?.id,
        files,
      });

      // Erros do backend (ex.: código repetido) já aparecem no toast do interceptor
      if (!ok) return;

      toast.success("Sangria registrada! Ela vira saldo após a aprovação do financeiro.");
      reset();
      setIsOpen(false);
      onCreated();
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <>
      <Button onClick={() => setIsOpen(true)} className="gap-2">
        <Plus className="h-4 w-4" />
        Nova Sangria
      </Button>

      <Dialog
        open={isOpen}
        onOpenChange={(open) => {
          if (isSaving) return;
          setIsOpen(open);
          if (!open) reset();
        }}
      >
        <DialogContent className="max-w-lg max-h-[95vh] overflow-y-auto overflow-x-hidden">
          <DialogHeader>
            <DialogTitle>Nova Sangria</DialogTitle>
            <DialogDescription className="font-medium text-amber-600">
              Confira se está colocando todos os dados corretos.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 min-w-0">
            {selectSeller && (
              <div className="space-y-2">
                <Label>Vendedor</Label>
                <SearchableSelect
                  items={sellers!}
                  searchFields={["name"]}
                  displayField="name"
                  placeholder="Selecione o vendedor"
                  value={seller}
                  onSelect={(item) => {
                    setSeller(item);
                    setErrors((prev) => ({ ...prev, seller: "" }));
                  }}
                />
                {errors.seller && <p className="text-sm text-red-500">{errors.seller}</p>}
              </div>
            )}

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="bleed-code">Código da sangria</Label>
                <Input
                  id="bleed-code"
                  inputMode="numeric"
                  maxLength={5}
                  placeholder="5 dígitos (ex.: 01234)"
                  value={code}
                  onChange={(e) => {
                    setCode(e.target.value.replace(/\D/g, "").slice(0, 5));
                    setErrors((prev) => ({ ...prev, code: "" }));
                  }}
                />
                {errors.code ? (
                  <p className="text-sm text-red-500">{errors.code}</p>
                ) : (
                  <p className={`text-xs ${code.length === 5 ? "text-green-600" : "text-muted-foreground"}`}>
                    {code.length}/5 dígitos
                  </p>
                )}
              </div>

              <div className="space-y-2">
                <Label htmlFor="bleed-date">Data da sangria</Label>
                <Input
                  id="bleed-date"
                  type="date"
                  value={bleedDate}
                  onChange={(e) => setBleedDate(e.target.value)}
                />
                {errors.bleed_date && <p className="text-sm text-red-500">{errors.bleed_date}</p>}
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="bleed-value">Valor</Label>
              <Input
                key={valueKey}
                id="bleed-value"
                typeMask="currency"
                inputMode="numeric"
                placeholder="R$ 0,00"
                onChange={(e) => setValue(Number(e.target.value.replace(/\D/g, "")) / 100)}
              />
              {errors.value && <p className="text-sm text-red-500">{errors.value}</p>}
            </div>

            <div className="space-y-2">
              <Label htmlFor="bleed-observation">Observação</Label>
              <Textarea
                id="bleed-observation"
                rows={2}
                maxLength={1000}
                value={observation}
                onChange={(e) => setObservation(e.target.value)}
              />
            </div>

            <div className="space-y-2 min-w-0">
              <Label>
                Anexo <span className="text-red-500">(obrigatório)</span>
              </Label>
              <div className="flex items-stretch gap-2">
                <button
                  type="button"
                  className="flex-1 border-2 border-dashed border-orange-300 rounded-lg p-4 text-center hover:border-orange-500 transition-colors"
                  onClick={() => fileInputRef.current?.click()}
                  onDragOver={(e) => e.preventDefault()}
                  onDrop={(e) => {
                    e.preventDefault();
                    addFiles(Array.from(e.dataTransfer.files));
                  }}
                >
                  <Upload className="h-7 w-7 mx-auto text-orange-400 mb-1" />
                  <p className="text-sm text-muted-foreground">
                    Clique para selecionar ou arraste os arquivos
                  </p>
                  <p className="text-xs text-muted-foreground mt-1">
                    JPG, PNG, WEBP ou PDF (máx. 10 MB cada)
                  </p>
                </button>
                <button
                  type="button"
                  onClick={() => setIsCameraOpen(true)}
                  className="flex flex-col items-center justify-center w-24 border-2 border-dashed border-orange-300 rounded-lg hover:border-orange-500 transition-colors"
                  title="Tirar foto"
                >
                  <Camera className="w-7 h-7 mb-1 text-orange-400" />
                  <span className="text-xs text-muted-foreground font-semibold">Tirar foto</span>
                </button>
                <input
                  ref={fileInputRef}
                  type="file"
                  multiple
                  accept="image/jpeg,image/png,image/webp,application/pdf"
                  className="hidden"
                  onChange={(e) => {
                    addFiles(Array.from(e.target.files || []));
                    e.target.value = "";
                  }}
                />
              </div>

              {files.length > 0 && (
                <div className="space-y-1">
                  {files.map((file, index) => (
                    <div
                      key={`${file.name}-${index}`}
                      className="flex items-center justify-between gap-2 rounded-lg border p-2 min-w-0"
                    >
                      <div className="flex flex-1 items-center gap-2 min-w-0">
                        {file.type === "application/pdf" ? (
                          <FileText className="h-4 w-4 shrink-0 text-gray-400" />
                        ) : (
                          <ImageIcon className="h-4 w-4 shrink-0 text-gray-400" />
                        )}
                        <span className="text-sm truncate" title={file.name}>{file.name}</span>
                      </div>
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        className="h-6 w-6 shrink-0 text-red-500 hover:text-red-700"
                        onClick={() => setFiles((prev) => prev.filter((_, i) => i !== index))}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  ))}
                </div>
              )}
              {errors.files && <p className="text-sm text-red-500">{errors.files}</p>}
            </div>
          </div>

          <DialogFooter className="gap-2 sm:gap-0">
            <Button
              type="button"
              variant="outline"
              disabled={isSaving}
              onClick={() => {
                setIsOpen(false);
                reset();
              }}
            >
              Cancelar
            </Button>
            <Button type="button" onClick={handleSubmit} disabled={isSaving}>
              {isSaving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Criar Sangria
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <CameraModal
        isOpen={isCameraOpen}
        onClose={() => setIsCameraOpen(false)}
        onCapture={(file) => {
          addFiles([file]);
          setIsCameraOpen(false);
        }}
      />
    </>
  );
}
